import * as React from "react";
import { css } from "@emotion/react";
import f from "@/module/Utils/emotion";
import v from "@/module/Utils/variable";
import Util from "@/module/Utils/Util";
import { useInView } from "react-intersection-observer";

// Components
import Picture from "@/components/Atoms/Picture";
import DescriptionFloatNav from "@/components/Organisms/DescriptionFloatNav";
import ReportModal from "@/components/Organisms/ReportModal";

// Images
import SeeUbb from "@/images/common/text_see_ubb.svg";
import Lisa from "@/images/opening/lisa.png";
import Profile from "@/images/common/text_profile.svg";
import QuestionIcon from "@/images/start/icon_question.svg";
import RectIcon from "@/images/common/icon_rect";

// Data
import textData from "@/module/data/texts/start";
import Link from "@/components/Atoms/Link";

// Interfaces
interface DescriptionProps {
  onPageMove: () => void;
}

const Description: React.FC<DescriptionProps> = (props) => {
  // Initialize
  const [ubbRef, ubbInView, ubbEntry] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const [risaRef, risaInView, risaEntry] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const [overviewRef, overviewInView, overviewEntry] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const [modalShowFlag, setModalShowFlag] = React.useState(false);

  // Styles
  const topDescriptionWrapStyle = css({
    [f.sp()]: {
      paddingTop: f.vw(206.5, v.spWidth),
    },
    [f.pc()]: {
      position: "relative",
      zIndex: 1,
      paddingTop: f.vw(20, v.pcWidth),
    },
  });

  const topSeeUbbTitleStyle = css({
    [f.sp()]: {
      width: f.vw(351.42, v.spWidth),
      margin: `${f.vw(0, v.spWidth)} auto 0 auto`,
    },
    [f.pc()]: {
      width: f.vw(418, v.pcWidth),
      margin: `${f.vw(150, v.pcWidth)} auto 0 auto`,
    },
  });

  const textInViewAnimation = css({
    opacity: 0,
    transform: "translateY(10px)",
    transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
    "&.is-animate": {
      opacity: 1,
      transform: "translateY(0)",
    },
  });

  const topSeeUbbTextStyle = css(textInViewAnimation, {
    color: "#fff",
    ...f.regularFont(),
    [f.sp()]: {
      width: f.vw(650, v.spWidth),
      margin: `${f.vw(97.3, v.spWidth)} auto 0 auto`,
      fontSize: f.vw(28, v.spWidth),
      lineHeight: 46 / 28,
    },
    [f.pc()]: {
      width: f.vw(578, v.pcWidth),
      margin: `${f.vw(75, v.pcWidth)} auto 0 auto`,
      fontSize: f.vw(18, v.pcWidth),

      lineHeight: 1.7,
    },
  });

  const topLisaNameStyle = css({
    color: "#fff",
    [f.sp()]: {
      marginTop: f.vw(110, v.spWidth),
      fontSize: f.vw(34, v.spWidth),
      textAlign: "center",
    },
    [f.pc()]: {
      marginTop: f.vw(40, v.pcWidth),
      fontSize: f.vw(25, v.pcWidth),
      textAlign: "center",
    },
    span: {
      [f.sp()]: {
        display: "inline-block",
        fontSize: f.vw(20, v.spWidth),
        marginBottom: f.vw(16, v.spWidth),
      },
      [f.pc()]: {
        display: "inline-block",
        fontSize: f.vw(14, v.pcWidth),
        marginBottom: f.vw(10, v.pcWidth),
      },
    },
  });

  const lisaStyle = css({
    [f.sp()]: {
      width: f.vw(445.97, v.spWidth),
      margin: `${f.vw(27, v.spWidth)} auto 0 auto`,
    },
    [f.pc()]: {
      width: f.vw(382, v.pcWidth),
      margin: `${f.vw(38, v.pcWidth)} auto 0 auto`,
    },
  });

  const topProfileTextStyle = css({
    [f.sp()]: {
      width: f.vw(95.15, v.spWidth),
      margin: `${f.vw(53.6, v.spWidth)} auto 0 auto`,
    },
    [f.pc()]: {
      width: f.vw(46, v.pcWidth),
      margin: `${f.vw(45, v.pcWidth)} auto 0 auto`,
    },
  });

  const topLisaDescriptionStyle = css({
    color: "#fff",
    ...f.regularFont(),
    [f.sp()]: {
      width: f.vw(642, v.spWidth),
      margin: `${f.vw(27.6, v.spWidth)} auto 0 auto`,
      fontSize: f.vw(20, v.spWidth),
      lineHeight: 32 / 20,
    },
    [f.pc()]: {
      width: f.vw(578, v.pcWidth),
      margin: `${f.vw(20, v.pcWidth)} auto 0 auto`,
      fontSize: f.vw(14, v.pcWidth),

      lineHeight: 1.7,
    },
  });

  const borderStyle = css({
    backgroundColor: "#fff",
    opacity: "0.3",
    [f.sp()]: {
      width: f.vw(650, v.spWidth),
      height: "1px",
      margin: `${f.vw(28.2, v.spWidth)} auto ${f.vw(38.8, v.spWidth)} auto`,
    },
    [f.pc()]: {
      width: f.vw(578, v.pcWidth),
      height: "1px",
      margin: `${f.vw(24, v.pcWidth)} auto ${f.vw(20, v.pcWidth)} auto`,
    },
  });

  const topLastDescriptionTextStyle = css(textInViewAnimation, {
    color: "#fff",
    ...f.regularFont(),
    letterSpacing: "0.07em",
    [f.sp()]: {
      width: f.vw(650, v.spWidth),
      margin: `0 auto`,
      fontSize: f.vw(28, v.spWidth),
      lineHeight: 1.7,
    },
    [f.pc()]: {
      width: f.vw(578, v.pcWidth),
      margin: `${f.vw(0, v.pcWidth)} auto 0 auto`,
      fontSize: f.vw(18, v.pcWidth),
      lineHeight: 1.8,
    },
  });

  const buttonWrapStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  });

  const reportButtonStyle = css({
    color: "#fff",
    lineHeight: 1.4,
    [f.sp()]: {
      marginTop: f.vw(92, v.spWidth),
      fontSize: Util.isLang("ja") ? f.vw(32, v.spWidth) : f.vw(26, v.spWidth),
    },
    [f.pc()]: {
      marginTop: f.vw(45),
      fontSize: f.vw(14),
    },
    span: {
      position: "relative",
      [f.sp()]: {
        paddingRight: f.vw(65, v.spWidth),
      },
      [f.pc()]: {
        paddingRight: f.vw(30),
      },
      "&:after": {
        content: `""`,
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
        backgroundImage: `url(${QuestionIcon})`,
        backgroundSize: "99% 99%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top left",
        [f.sp()]: {
          width: f.vw(45, v.spWidth),
          height: f.vw(45, v.spWidth),
        },
        [f.pc()]: {
          width: f.vw(22),
          height: f.vw(22),
        },
      },
    },
  });

  const storiesButtonStyle = css({
    color: "#fff",
    lineHeight: 1.4,
    [f.sp()]: {
      marginTop: f.vw(47, v.spWidth),
      fontSize: f.vw(32, v.spWidth),
    },
    [f.pc()]: {
      marginTop: f.vw(20),
      fontSize: f.vw(14),
    },
    span: {
      position: "relative",
      [f.sp()]: {
        paddingRight: f.vw(50, v.spWidth),
      },
      [f.pc()]: {
        paddingRight: f.vw(24.35),
      },
      "&:after": {
        content: `""`,
        position: "absolute",
        top: "50%",
        right: 0,
        transform: "translateY(-50%)",
        backgroundImage: `url(${RectIcon("#fff")})`,
        backgroundSize: "99% 99%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top left",
        [f.sp()]: {
          width: f.vw(30.17, v.spWidth),
          height: f.vw(30.17, v.spWidth),
        },
        [f.pc()]: {
          width: f.vw(16.35),
          height: f.vw(16.35),
        },
      },
    },
  });

  const catchCopyTextStyle = css({
    textAlign: "center",
    color: "#fff",
    [f.sp()]: {
      marginTop: f.vw(150, v.spWidth),
      fontSize: f.vw(44, v.spWidth),
    },
    [f.pc()]: {
      marginTop: f.vw(75),
      fontSize: f.vw(24),
    },
  });

  // Logic
  const handlePageLeave = async (resolve) => {
    props.onPageMove();
    await Util.sleep(v.pageTransition);
    resolve();
  };

  // Lifecycle
  React.useEffect(() => {
    if (ubbEntry && ubbInView) {
      ubbEntry.target.classList.add("is-animate");
    }
  }, [ubbInView]);

  React.useEffect(() => {
    if (risaEntry && risaInView) {
      risaEntry.target.classList.add("is-animate");
    }
  }, [risaInView]);

  React.useEffect(() => {
    if (overviewEntry && overviewInView) {
      overviewEntry.target.classList.add("is-animate");
    }
  }, [overviewInView]);

  React.useEffect(() => {
    document.body.scrollTo(0, 0);
    Util.removeTouchActions();
  }, []);

  return (
    <div css={topDescriptionWrapStyle}>
      <div>
        <h2 css={topSeeUbbTitleStyle}>
          <Picture path={SeeUbb} alt={"See UBB"} />
        </h2>
        <div css={topSeeUbbTextStyle} ref={ubbRef}>
          {textData.seeUbbDescription}
        </div>
        <div css={textInViewAnimation} ref={risaRef}>
          <p css={topLisaNameStyle}>
            <span>{textData.risaTitle}</span>
            <br />
            {textData.risaName}
          </p>
          <div css={lisaStyle}>
            <Picture path={Lisa} alt={""} />
          </div>
          <div css={topProfileTextStyle}>
            <Picture path={Profile} alt={"profile"} />
          </div>
          <p css={topLisaDescriptionStyle}>{textData.profile}</p>
        </div>
        <div css={borderStyle}></div>
        <p css={topLastDescriptionTextStyle} ref={overviewRef}>
          {textData.comment}
        </p>
        <div css={buttonWrapStyle}>
          <div css={reportButtonStyle}>
            <button
              type={"button"}
              onClick={() => {
                setModalShowFlag(true);
              }}
              className={"u-ga-seeubb-start_btn-about"}
            >
              <span>{textData.researchLink}</span>
            </button>
          </div>
          <div css={storiesButtonStyle}>
            <Link
              to={"/stories/"}
              onBeforeLeave={handlePageLeave}
              gaClass={"u-ga-seeubb-start_btn-stories"}
            >
              <span>{textData.ubbLink}</span>
            </Link>
          </div>
          <p css={catchCopyTextStyle}>{textData.title}</p>
        </div>
        <ReportModal
          onModalClose={() => {
            setModalShowFlag(false);
          }}
          isShow={modalShowFlag}
        />
      </div>
      <DescriptionFloatNav
        isShow={true}
        link={"/question/"}
        buttonType={"seeubb"}
        onClickButton={props.onPageMove}
        gaPage={"start"}
      />
    </div>
  );
};

export default Description;
